import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import ClockIcon from "../../../../assets/img/action-center/clock-green.svg";
import ChevronRightSmIcon from "../../../../assets/img/action-center/chevron-right-sm.svg";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import PromoExpiryReviewModal from "../promo-expiry-modal/PromoExpiryReviewModal";
import {
  CARD_BODY,
  CARD_CHIP_LABEL,
  CARD_HEADLINE,
  CARD_VISIBLE_ROWS,
  DISMISS_LABEL,
  REVIEW_AND_EXTEND_LABEL,
} from "../promoExpiryConstants";
import { hiddenRowCount, statusPillLabel } from "../promoExpiryUtils";
import "./PromoExpiryCard.scss";

/**
 * One grouped card per property, listing every promo expired in the last 14 days and expiring in the
 * next 14 — not one card per promo.
 *
 * The headline and body are static: the card covers a mixed expired/expiring set, so any single-promo
 * sentence would be wrong for the rest of the list.
 */
const PromoExpiryCard = ({
  data,
  onDismiss,
  onRefresh,
  cardPosition,
  hotelName,
  // EO-1184: an expiry email link lands on the home page and opens this
  // card's Review modal. The card already owns that modal's state, so the
  // link only has to ask.
  autoOpenReview = false,
  onReviewAutoOpened,
}) => {
  const cardRef = useHeapCardViewed("promo_expiry", cardPosition, hotelName);
  const [expanded, setExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Opened once per arrival. onReviewAutoOpened strips the link's params so a
  // refresh, or closing the modal, does not reopen it.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!autoOpenReview || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    setIsModalOpen(true);
    onReviewAutoOpened?.();
  }, [autoOpenReview, onReviewAutoOpened]);

  const promos = data?.promos || [];
  if (promos.length === 0) return null;

  const hidden = hiddenRowCount(promos.length, CARD_VISIBLE_ROWS);
  const visible = expanded ? promos : promos.slice(0, CARD_VISIBLE_ROWS);

  // ⚠ Dismiss every row in the payload, not only the visible ones. Sending just the first three leaves
  // the hidden rows to return alone in tomorrow's card, which reads as the dismissal having failed.
  const handleMaybeLater = () => {
    onDismiss?.(promos.map((promo) => promo.recommendationId).filter(Boolean));
  };

  const formatRevenue = (promo) =>
    promo.revenue === null || promo.revenue === undefined
      ? ""
      : `$${Number(promo.revenue).toLocaleString()}`;

  return (
    <>
      <div className="promo-expiry-card" ref={cardRef}>
        <div className="promo-expiry-card-chip">
          <img src={ClockIcon} alt="" />
          <span>{CARD_CHIP_LABEL}</span>
        </div>

        <h3 className="promo-expiry-card-headline">{CARD_HEADLINE}</h3>
        <p className="promo-expiry-card-body">{CARD_BODY}</p>

        <ul className="promo-expiry-card-list">
          {visible.map((promo) => (
            <li key={promo.recommendationId ?? promo.promoId}>
              <span className="promo-expiry-card-name">{promo.promoName}</span>
              <span className="promo-expiry-card-pill">
                {statusPillLabel(promo.daysLeft)}
              </span>
              <span className="promo-expiry-card-revenue">
                {formatRevenue(promo)}
              </span>
            </li>
          ))}
        </ul>

        {hidden > 0 && !expanded && (
          <button
            type="button"
            className="promo-expiry-card-more"
            aria-expanded={false}
            aria-controls="promo-expiry-card-list"
            onClick={() => setExpanded(true)}
            data-heap-id="promo-expiry-show-more"
          >
            {`+ ${hidden} more`}
          </button>
        )}

        <div className="promo-expiry-card-actions">
          <button
            type="button"
            className="promo-expiry-card-later"
            onClick={handleMaybeLater}
            data-heap-id="promo-expiry-dismiss"
          >
            {DISMISS_LABEL}
          </button>
          <Button
            appearance="ghost"
            className="promo-expiry-card-review"
            onClick={() => setIsModalOpen(true)}
            data-heap-id="promo-expiry-review-extend"
          >
            {REVIEW_AND_EXTEND_LABEL}{" "}
            <img src={ChevronRightSmIcon} alt="" className="btn-chevron" />
          </Button>
        </div>
      </div>

      {isModalOpen && (
        <PromoExpiryReviewModal
          promos={promos}
          onClose={() => setIsModalOpen(false)}
          // Refetches the action-center feeds so a promo whose dates were extended drops off this card.
          // Nothing in the modal writes back to the card's own state, so without this the row it just
          // saved stays on screen reading "expiring", which looks like the save did not take.
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

PromoExpiryCard.propTypes = {
  data: PropTypes.shape({
    expiringCount: PropTypes.number,
    expiredCount: PropTypes.number,
    promos: PropTypes.arrayOf(
      PropTypes.shape({
        recommendationId: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        promoId: PropTypes.string,
        promoName: PropTypes.string,
        daysLeft: PropTypes.number,
        expired: PropTypes.bool,
        revenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }).isRequired,
  onDismiss: PropTypes.func,
  onRefresh: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default PromoExpiryCard;
